import { SiteState } from '@treadinc/horizon-api-spec';
import axios, { AxiosError } from 'axios';
import { get } from 'lodash-es';

import { geoFenceCircleOption, geofenceNoneOption } from '~constants/enums';
import { CompanyBasic } from '~hooks/useCompany/models';
import { WayPoint } from '~hooks/useSites/models';
import { Nullable } from '~types/Nullable';

import { ProjectFormSchemaInterface } from '../Projects/projectFormSchema';
import { NewOrderFormSchemaInterface } from './newOrderFormSchema';

type SiteAddress = { streetAddress?: string; placeId?: string } | null | undefined;

interface CircleGeofencePayload {
  name: string | undefined;
  radius: number;
  circleCenter: {
    lat: number | null | undefined;
    lon: number | null | undefined;
  };
  geofenceType: typeof geoFenceCircleOption;
}

type CircleGeofence = CircleGeofencePayload | Record<string, never>;

interface CreateSiteInlinePayload {
  name: string;
  company: {
    id: string | undefined;
    legal_name: string | undefined;
  };
  address: SiteAddress;
  lat: string;
  lon: string;
  state: SiteState;
  geoFence: CircleGeofence;
  geofenceType?: typeof geoFenceCircleOption | typeof geofenceNoneOption;
  latLon: string;
  radius?: number;
  circleCenter?: {
    lat: number | null | undefined;
    lon: number | null | undefined;
  };
}

interface CreatedSite {
  id: string;
}

const hasCircleGeofenceData = (
  geofence: CircleGeofence,
): geofence is CircleGeofencePayload => 'radius' in geofence;

const isAddressValid = (address: SiteAddress): boolean =>
  !!address && (Boolean(address.streetAddress) || Boolean(address.placeId));

export const createSitesInline = async <
  CreateSitePayload,
  CreateSiteResponse extends CreatedSite | undefined,
>(
  data: NewOrderFormSchemaInterface | ProjectFormSchemaInterface,
  createNewSite: (siteData: CreateSitePayload) => Promise<CreateSiteResponse>,
  defaultRadius?: Nullable<number>,
) => {
  if (
    data.pickUpWayPoint &&
    'site' in data.pickUpWayPoint &&
    data.pickUpWayPoint.site &&
    !data.pickUpWayPoint?.site?.id &&
    isAddressValid(data.pickUpWayPoint.site.address)
  ) {
    // Site.id is empty when an address from the SiteLocation is selected
    // This site is not in the list of sites, and needs to be added.
    const company = data.company as CompanyBasic;
    const geofence = createCircleGeofence(defaultRadius, data.pickUpWayPoint as WayPoint);

    try {
      const pickUpSitePayload: CreateSiteInlinePayload = {
        name: data.pickUpWayPoint?.site?.name || '',
        company: { id: company?.id, legal_name: company?.legalName },
        address: data.pickUpWayPoint?.site?.address,
        lat: String(data.pickUpWayPoint?.site?.lat),
        lon: String(data.pickUpWayPoint?.site?.lon),
        state: SiteState.ACTIVE,
        geoFence: geofence,
        geofenceType: hasCircleGeofenceData(geofence)
          ? geoFenceCircleOption
          : geofenceNoneOption,
        latLon: `${String(data.pickUpWayPoint?.site?.lat)}, ${String(data.pickUpWayPoint?.site?.lon)}`,
        ...(hasCircleGeofenceData(geofence)
          ? { radius: geofence.radius, circleCenter: geofence.circleCenter }
          : {}),
      };
      const pickUpSite = await createNewSite(pickUpSitePayload as CreateSitePayload);
      if (!pickUpSite?.id) {
        return { error: true, field: 'pickUpWayPoint' };
      }
      data.pickUpWayPoint.site.id = pickUpSite.id;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const is422 = String((error as AxiosError).status) === '422';
        const message = get(error, 'response.data.error.errors[0].message');

        if (is422 && message) {
          return { error: true, field: 'pickUpWayPoint', message };
        }
      }

      return { error: true, field: 'pickUpWayPoint' };
    }
  }

  if (
    data.dropOffWayPoint &&
    'site' in data.dropOffWayPoint &&
    data.dropOffWayPoint.site &&
    !data.dropOffWayPoint?.site?.id &&
    isAddressValid(data.dropOffWayPoint.site.address)
  ) {
    // Site.id is empty when an address from the SiteLocation is selected
    // This site is not in the list of sites, and needs to be added.
    const company = data.company as CompanyBasic;
    const geofence = createCircleGeofence(
      defaultRadius,
      data.dropOffWayPoint as WayPoint,
    );

    try {
      const dropOffSitePayload: CreateSiteInlinePayload = {
        name: data.dropOffWayPoint?.site?.name || '',
        company: { id: company?.id, legal_name: company?.legalName },
        address: data.dropOffWayPoint?.site?.address,
        lat: String(data.dropOffWayPoint?.site?.lat),
        lon: String(data.dropOffWayPoint?.site?.lon),
        state: SiteState.ACTIVE,
        geoFence: geofence,
        latLon: `${String(data.dropOffWayPoint?.site?.lat)},${String(data.dropOffWayPoint?.site?.lon)}`,
        ...(hasCircleGeofenceData(geofence)
          ? {
              geofenceType: geofence.geofenceType,
              radius: geofence.radius,
              circleCenter: geofence.circleCenter,
            }
          : {}),
      };
      const dropOffSite = await createNewSite(dropOffSitePayload as CreateSitePayload);
      if (!dropOffSite?.id) {
        return { error: true, field: 'dropOffWayPoint' };
      }
      data.dropOffWayPoint.site.id = dropOffSite.id;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const is422 = String((error as AxiosError).status) === '422';
        const message = get(error, 'response.data.error.errors[0].message');

        if (is422 && message) {
          return { error: true, field: 'dropOffWayPoint', message };
        }
      }

      return { error: true, field: 'dropOffWayPoint' };
    }
  }

  return data;
};

const createCircleGeofence = (
  radius: Nullable<number>,
  waypoint: WayPoint,
): CircleGeofence => {
  if (!waypoint || !radius) {
    return {};
  }

  return {
    name: waypoint.site?.name,
    radius: Number(radius),
    circleCenter: { lat: waypoint.site?.lat, lon: waypoint.site?.lon },
    geofenceType: geoFenceCircleOption,
  };
};
